import { AnswerForType, FormFieldOptions } from "@tellescope/types-models";
import { useCallback } from "react";
import { convertFormFieldType, normalizeFieldOptions } from "../../utils";
import { FieldChangeOptions, FormTypesWithValues } from "../../types";
import type { useTellescopeFormCore } from "../use-tellescope-form-core/use-tellescope-form-core";
import { isRepeatMismatch } from "../use-tellescope-form-core/resolve-repeat-mismatch";

export interface RepeatInputProps {
  repeatValue: string;
  onRepeatChange: (newValue: string) => void;
  onRepeatBlur: () => void;
  repeatTouched: boolean;
  repeatMismatch: boolean;
}

export interface UseGetFieldPropsArgs {
  core: ReturnType<typeof useTellescopeFormCore>;
  fieldErrors: Record<string, string | null>;
  setFieldValueWithAutoAdvance: <TField extends FormTypesWithValues>(
    fieldId: string,
    value: AnswerForType[TField],
    changeOptions?: FieldChangeOptions
  ) => void;
}

export const useGetFieldProps = ({ core, fieldErrors, setFieldValueWithAutoAdvance }: UseGetFieldPropsArgs) =>
  useCallback(
    <TField extends FormTypesWithValues = FormTypesWithValues>(
      fieldId: string,
      type: TField
    ): {
      id: string;
      type: TField;
      title: string;
      value: AnswerForType[TField];
      onChange: (newValue: AnswerForType[TField], changeOptions?: FieldChangeOptions) => void;
      onBlur?: () => void;
      error: string | null;
      touched: boolean;
      options?: FormFieldOptions;
      required?: boolean;
      fullZIP?: boolean;
      description?: string;
      htmlDescription?: string;
      repeat?: RepeatInputProps;
      disabled?: boolean;
      pendingFiles?: File[];
      onPendingFilesChange?: (files: File[]) => void;
    } | null => {
      const fieldResponse = core.currentResponses[fieldId];
      const fieldMetadata = core.visibleFields.find((f) => f.id === fieldId) ?? core.fields?.fieldsMap[fieldId];

      if (!fieldResponse || !fieldMetadata) {
        return null;
      }

      if (type !== convertFormFieldType(fieldMetadata.type)) {
        return null;
      }

      const touched = core.touchedFields.has(fieldId);

      const onChange = (newValue: AnswerForType[TField], changeOptions?: FieldChangeOptions) => {
        setFieldValueWithAutoAdvance(fieldId, newValue, changeOptions);
        if (changeOptions?.skipValidation) return;
        core.markFieldTouched(fieldId);
      };

      const onBlur = () => core.handleFieldBlur(fieldId);

      const supportsRepeat =
        fieldMetadata.options && "repeat" in fieldMetadata.options && !!fieldMetadata.options?.repeat;
      const repeat: RepeatInputProps | undefined = supportsRepeat
        ? {
            repeatValue: core.repeatInputs[fieldId] ?? "",
            onRepeatChange: (newValue) => core.setRepeatValue(fieldId, newValue),
            onRepeatBlur: () => core.handleRepeatBlur(fieldId),
            repeatTouched: core.repeatTouchedFields.has(fieldId),
            repeatMismatch: isRepeatMismatch({
              field: fieldMetadata,
              value: fieldResponse.answer.value,
              confirmation: core.repeatInputs[fieldId] ?? "",
            }),
          }
        : undefined;

      return {
        id: fieldId,
        type,
        title: core.interpolateFieldText.text(fieldMetadata.title) ?? "",
        value: fieldResponse.answer.value as AnswerForType[TField],
        onChange,
        onBlur,
        error: touched ? fieldErrors[fieldId] ?? null : null,
        touched,
        // Deliberately untranslated: `options` carries the values that get stored and compared, and
        // choice fields translate their own labels at render (see translateFieldOptionLabels).
        options: normalizeFieldOptions(fieldMetadata.options),
        required: !fieldMetadata.isOptional,
        fullZIP: fieldMetadata.fullZIP,
        description: core.interpolateFieldText.text(fieldMetadata.description),
        htmlDescription: core.interpolateFieldText.html(fieldMetadata.htmlDescription),
        repeat,
        disabled: fieldResponse.disabled,
        pendingFiles: core.pendingFiles[fieldId],
        onPendingFilesChange: (files: File[]) => core.setPendingFilesForField(fieldId, files),
      };
    },
    [core, fieldErrors, setFieldValueWithAutoAdvance]
  );
